import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { formatDunsNumber } from '../../dataHandlers/riskCompliancePaths';
import {
  getRiskBodyTextSx,
  getRiskPreviewLabelSx,
  getRiskPreviewPanelSx,
  getRiskPreviewValueSx,
  getRiskSectionTitleSx,
  riskButtonSx,
  riskSectionCardSx,
  riskToggleGroupSx,
} from './riskComplianceStyles';
import DunsNoMatchAlert from './DunsNoMatchAlert';

const MODE_MANUAL = 'manual';
const MODE_RESOLVE = 'resolve';

export default function DunsRequiredCard({
  customer,
  labels,
  onValidateAndLink,
  onResolve,
  onInvestigate,
  isLinking,
  isResolving,
  isInvestigating,
  initialMode = MODE_MANUAL,
  noMatchState = null,
}) {
  const theme = useTheme();
  const [mode, setMode] = useState(initialMode);
  const [dunsInput, setDunsInput] = useState('');

  const resolvePreview = useMemo(() => {
    const address = customer?.postal_address;
    const street = address?.street_name || address?.address_line || '—';
    const country = address?.country
      ? `${address.country}${address.country_code ? ` (${address.country_code})` : ''}`
      : '—';
    return {
      businessName: customer?.party_name || customer?.name || '—',
      address: street,
      country,
      registrationNumber: customer?.company_id || customer?.tin || '—',
    };
  }, [customer]);

  const handleManualSubmit = () => {
    onValidateAndLink({
      dunsNumber: dunsInput.replace(/\D/g, ''),
      source: 'manual',
    });
  };

  const showNoMatchAlert = noMatchState === 'noMatch' || noMatchState === 'investigationRequested';
  const investigationRequested = noMatchState === 'investigationRequested';

  return (
    <Box sx={riskSectionCardSx}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <PublicOutlinedIcon color="action" fontSize="small" />
        <Typography variant="subtitle1" sx={getRiskSectionTitleSx(theme)}>
          {labels.title}
        </Typography>
      </Stack>

      <Typography variant="body2" sx={{ ...getRiskBodyTextSx(theme), mb: 3 }}>
        {labels.description}
      </Typography>

      <ToggleButtonGroup
        exclusive
        value={mode}
        onChange={(_event, value) => value && setMode(value)}
        sx={riskToggleGroupSx}
      >
        <ToggleButton value={MODE_MANUAL}>
          <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 1 }} />
          {labels.modeManual}
        </ToggleButton>
        <ToggleButton value={MODE_RESOLVE}>
          <SearchOutlinedIcon fontSize="small" sx={{ mr: 1 }} />
          {labels.modeResolve}
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === MODE_MANUAL ? (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'flex-end' }}>
          <TextField
            label={labels.dunsInputLabel}
            placeholder={labels.dunsPlaceholder}
            value={dunsInput}
            onChange={(event) => setDunsInput(formatDunsNumber(event.target.value.replace(/\D/g, '').slice(0, 9)))}
            sx={{ width: '100%', maxWidth: { xs: '100%', sm: 280 } }}
          />
          <Button
            variant="contained"
            onClick={handleManualSubmit}
            disabled={isLinking || dunsInput.replace(/\D/g, '').length !== 9}
            sx={{ ...riskButtonSx, minWidth: 160, whiteSpace: 'nowrap', height: 40 }}
          >
            {isLinking ? <CircularProgress size={20} color="inherit" /> : labels.validateLink}
          </Button>
        </Stack>
      ) : (
        <Box>
          <Box sx={getRiskPreviewPanelSx(theme)}>
            <Typography
              variant="body2"
              sx={{ ...getRiskPreviewLabelSx(theme), mb: 2, fontWeight: theme.typography.fontWeightMedium }}
            >
              {labels.resolvePreviewHeader}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <PreviewRow theme={theme} label={labels.businessName} value={resolvePreview.businessName} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <PreviewRow theme={theme} label={labels.country} value={resolvePreview.country} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <PreviewRow theme={theme} label={labels.address} value={resolvePreview.address} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <PreviewRow theme={theme} label={labels.registrationNumber} value={resolvePreview.registrationNumber} />
              </Grid>
            </Grid>
          </Box>

          {!investigationRequested && (
            <Button
              variant="contained"
              startIcon={isResolving ? <CircularProgress size={18} color="inherit" /> : <SearchOutlinedIcon />}
              onClick={onResolve}
              disabled={isResolving}
              sx={riskButtonSx}
            >
              {labels.resolveDuns}
            </Button>
          )}

          {showNoMatchAlert && (
            <DunsNoMatchAlert
              labels={labels.noMatch ?? {}}
              onInvestigate={onInvestigate}
              isInvestigating={isInvestigating}
              investigationRequested={investigationRequested}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function PreviewRow({ theme, label, value }) {
  return (
    <Box>
      <Typography variant="body2" sx={getRiskPreviewLabelSx(theme)}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={getRiskPreviewValueSx(theme)}>
        {value}
      </Typography>
    </Box>
  );
}
